/**
 * playSceneSpawns.ts — enemy spawn dispatch helper for PlayScene.
 *
 * Maps EnemySpawnData.type strings to concrete entity constructors and
 * appends them to the live gameState.enemies array. Split out of
 * PlayScene.ts so the scene file stays under the AGENTS.md 300-line
 * limit and so the type → constructor table is unit-testable in
 * isolation (a typo in this switch would otherwise only fail at
 * runtime when a level loads).
 *
 * No React imports. No Skia imports.
 *
 * @module playSceneSpawns
 */

import { gameState } from '../engine/gameState';
import { Nibbler } from '../entities/enemies/Nibbler';
import { Thornhop } from '../entities/enemies/Thornhop';
import { GloomBat } from '../entities/enemies/GloomBat';
// W3 greybox enemies. See ROADMAP Phase 4 — World 3 Foundation.
import { Shellback } from '../entities/enemies/Shellback';
import { SparkImp } from '../entities/enemies/SparkImp';
import { SentinelBloom } from '../entities/enemies/SentinelBloom';
import { Gearling } from '../entities/enemies/Gearling';
// W3 miniboss (Brasscloud Foundry) — armoured charger.
import { BrambleRam } from '../entities/enemies/BrambleRam';
// W4 miniboss (Brasscloud Foundry) — hammer+shield mech, steam stun windows.
import { ForgeWarden } from '../entities/enemies/ForgeWarden';
// W5 enemy (Eclipse Bastion) — ghost replaying Nova's position from 2 s ago.
import { ShadowGlider } from '../entities/enemies/ShadowGlider';
// W5 miniboss (Eclipse Bastion) — crystal clone mirroring Nova horizontally.
import { TideMirror } from '../entities/enemies/TideMirror';
// W3 boss (Brasscloud Foundry) — spawned in 3-B.
import { KingRivet } from '../entities/enemies/KingRivet';
// W4 boss (Crystal Tide Ruins) — spawned in 4-B.
import { PrismLeviathan, PRISM_LEVIATHAN_WIDTH } from '../entities/enemies/PrismLeviathan';
// W5 boss (Eclipse Bastion) — spawned in 5-B.
import { StarEaterBoss } from '../entities/enemies/StarEaterBoss';
import Logger from '../utils/logger';
import type { EnemySpawnData } from '../levels/TileMapLoader';

/**
 * Spawn enemy entities from level spawn data into gameState.enemies.
 *
 * The switch is exhaustive over `EnemySpawnData['type']`, so the `default`
 * branch is unreachable through the normal parse path. It exists to make the
 * failure loud rather than silent if a spawn ever reaches here unhandled.
 *
 * @param spawns - Enemy spawn definitions from level data.
 */
export function spawnEnemies(spawns: EnemySpawnData[]): void {
  for (const spawn of spawns) {
    switch (spawn.type) {
      case 'nibbler':        gameState.enemies.push(new Nibbler(spawn.x, spawn.y)); break;
      case 'thornhop':       gameState.enemies.push(new Thornhop(spawn.x, spawn.y)); break;
      case 'gloom_bat':      gameState.enemies.push(new GloomBat(spawn.x, spawn.y)); break;
      case 'shellback':      gameState.enemies.push(new Shellback(spawn.x, spawn.y)); break;
      case 'spark_imp':      gameState.enemies.push(new SparkImp(spawn.x, spawn.y)); break;
      case 'sentinel_bloom': gameState.enemies.push(new SentinelBloom(spawn.x, spawn.y)); break;
      case 'gearling':       gameState.enemies.push(new Gearling(spawn.x, spawn.y)); break;
      case 'bramble_ram':    gameState.enemies.push(new BrambleRam(spawn.x, spawn.y)); break;
      case 'forge_warden':   gameState.enemies.push(new ForgeWarden(spawn.x, spawn.y)); break;
      case 'shadow_glider':  gameState.enemies.push(new ShadowGlider(spawn.x, spawn.y)); break;
      // TideMirror: reflects Nova across `mirrorAxisX`, parsed by
      // enemySpawnParser as the Tiled object's horizontal centre. Fall back
      // to the clone's own spawn x — the entity's own constructor default —
      // which degrades to "mirrors about where it stands" rather than
      // silently snapping the clone to x = 0 on an unsized Tiled object.
      case 'tide_mirror':
        gameState.enemies.push(
          new TideMirror(spawn.x, spawn.y, spawn.mirrorAxisX ?? spawn.x),
        );
        break;
      // KingRivet: spawn.x/spawn.y is the Tiled top-left, same convention
      // as every other enemy. KingRivet's constructor parameter is named
      // `centerX/centerY` historically, but it stores the value into
      // `this.x` which is read as top-left by every collider/renderer
      // downstream (BaseEntity contract). So passing the Tiled top-left
      // through directly yields a boss whose visual top-left lives at
      // spawn.x/spawn.y — to centre the boss visually at arena (cx, cy),
      // author the Tiled object at (cx - W/2, cy - H/2). No conversion
      // needed here.
      case 'king_rivet':     gameState.enemies.push(new KingRivet(spawn.x, spawn.y)); break;
      // PrismLeviathan: same top-left contract as every other enemy in
      // this switch. The boss derives its internal patrol/bob centre as
      // (spawn.x + W/2, spawn.y + H/2). `patrolHalfWidth` is parsed from
      // the Tiled object's `width / 2` by enemySpawnParser; fall back to
      // half the boss's hitbox width so the default patrol DIAMETER
      // equals one body-length — keeps the boss from pinning, still
      // gives a small visible swim.
      case 'prism_leviathan':
        gameState.enemies.push(
          new PrismLeviathan(
            spawn.x,
            spawn.y,
            spawn.patrolHalfWidth ?? PRISM_LEVIATHAN_WIDTH / 2,
          ),
        );
        break;
      // StarEaterBoss: same top-left contract as KingRivet — the
      // constructor parameters are named `centerX/centerY` but are stored
      // straight into `this.x`/`this.y`, which every collider and renderer
      // downstream reads as the top-left (BaseEntity contract). So the
      // Tiled top-left passes through unconverted; to centre the titan at
      // arena (cx, cy), author the object at (cx - W/2, cy - H/2). The
      // titan is stationary on X, so it takes no patrol metadata.
      case 'star_eater_boss':
        gameState.enemies.push(new StarEaterBoss(spawn.x, spawn.y));
        break;
      default: {
        // DECISION: Assigning to `never` turns "a type was added to
        // EnemySpawnData without a case here" into a compile error, which is
        // the failure mode this file was extracted to catch. The log covers
        // the runtime case the compiler cannot see — a stale or hand-edited
        // level payload reaching the scene with a type the parser no longer
        // produces. Silently dropping it is how 222 inert spike tiles hid.
        const unhandled: never = spawn.type;
        Logger.warn('PlayScene', `Unhandled enemy spawn type, skipping: ${String(unhandled)}`);
        break;
      }
    }
  }
}
